import QRCode from "qrcode.react";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import toastError from "../../errors/toastError";

import { Button, CircularProgress, Dialog, DialogContent, Paper, Typography } from "@material-ui/core";
import { green } from "@material-ui/core/colors";
import { makeStyles } from "@material-ui/core/styles";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import { isPairingCode } from "../../helpers/passkeyWebAuthn";
import { i18n } from "../../translate/i18n";
import socket from "../../hooks/useSocket";

const useStyles = makeStyles(theme => ({
	buttonProgress: {
		color: green[500],
	},
	paperFlex: {
		display: "flex",
		flexDirection: "column",
		gap: "20px",
		alignItems: "center",
		paddingBottom: "15px"
	},
	pairingCode: {
		fontSize: "2rem",
		fontWeight: 700,
		letterSpacing: "0.35rem",
		fontFamily: "monospace",
	},
	extensionHint: {
		textAlign: "center",
		maxWidth: 420,
	},
}));

const QrcodeModal = ({ open, onClose, whatsAppId }) => {
	const classes = useStyles();
	const [qrCode, setQrCode] = useState("");
	const [connectionStatus, setConnectionStatus] = useState("");
	const [isLoading, setLoading] = useState(false);
	const [passkeyBridgeUrl, setPasskeyBridgeUrl] = useState("");
	const passkeyOpenedRef = useRef(false);
	const { user } = useContext(AuthContext);

	const applyWhatsappState = useCallback((whatsapp) => {
		if (!whatsapp) return;
		setConnectionStatus(whatsapp.status || "");
		if (whatsapp.qrcode) {
			setQrCode(whatsapp.qrcode);
		}
	}, []);

	const fetchSession = useCallback(async (signal) => {
		const { data } = await api.get(`/whatsapp/${whatsAppId}`, { signal });
		applyWhatsappState(data);
		setLoading(false);
		return data;
	}, [applyWhatsappState, whatsAppId]);

	const ensurePasskeyToken = useCallback(async () => {
		try {
			const { data } = await api.post(`/whatsappsession/${whatsAppId}/passkey-token`);
			if (data?.whatsAppWebUrl) {
				setPasskeyBridgeUrl(data.whatsAppWebUrl);
			}
			return data?.whatsAppWebUrl || "";
		} catch (err) {
			console.error({ err });
			return "";
		}
	}, [whatsAppId]);

	const openPasskeyBridge = useCallback(async (url) => {
		const bridgeUrl = url || passkeyBridgeUrl || (await ensurePasskeyToken());
		if (!bridgeUrl) {
			toastError(new Error(i18n.t("qrCode.passkeyTokenError")));
			return;
		}
		window.open(bridgeUrl, "_blank", "noopener,noreferrer");
	}, [ensurePasskeyToken, passkeyBridgeUrl]);

	useEffect(() => {
		if (!whatsAppId || !open) return;
		passkeyOpenedRef.current = false;
		let isMounted = true;
		const abortController = new AbortController();

		(async () => {
			try {
				setLoading(true);
				await Promise.all([
					fetchSession(abortController.signal),
					ensurePasskeyToken(),
				]);
			} catch (err) {
				if (!isMounted || err.name === "AbortError") return;
				console.error({ err });
				toastError(err);
				setLoading(false);
			}
		})();

		return () => {
			isMounted = false;
			abortController.abort();
		};
	}, [whatsAppId, open, fetchSession, ensurePasskeyToken]);

	useEffect(() => {
		if (!whatsAppId || !open) return;
		if (connectionStatus === "passkey" && !passkeyOpenedRef.current) {
			passkeyOpenedRef.current = true;
			openPasskeyBridge();
		}
	}, [connectionStatus, open, whatsAppId, openPasskeyBridge]);

	useEffect(() => {
		if (!whatsAppId) return;

		const whatsappEventName = `whatsapp-${user.adminId}`;
		const whatsappEventFunction = async (data) => {
			if (data.action !== "update" || data.whatsapp.id !== whatsAppId) return;

			applyWhatsappState(data.whatsapp);

			if (data.whatsapp.qrcode && data.whatsapp.qrcode !== "") {
				setLoading(false);
			}

			if (data.whatsapp.status === "CONNECTED") {
				onClose();
			}
		};

		socket.on(whatsappEventName, whatsappEventFunction);
		return () => {
			socket.off(whatsappEventName, whatsappEventFunction);
		};
	}, [whatsAppId, onClose, user.adminId, applyWhatsappState]);

	const showPairingCode = connectionStatus === "PAIRING" && isPairingCode(qrCode);
	const showQrCode = connectionStatus === "qrcode" && qrCode && !isPairingCode(qrCode);
	const showPasskeyPrompt = connectionStatus === "passkey";
	const showPhoneConfirm =
		connectionStatus === "qrcode_scanned" || connectionStatus === "authenticating";

	return (
		<Dialog
			open={open == true ? true : false}
			onClose={onClose}
			maxWidth="lg"
			scroll="paper"
		>
			<DialogContent>
				<Paper elevation={0} className={classes.paperFlex}>
					{showPairingCode ? (
						<>
							<Typography color="primary" gutterBottom align="center">
								{i18n.t("qrCode.pairingCodeMessage")}
							</Typography>
							<Typography className={classes.pairingCode}>{qrCode}</Typography>
							<Typography variant="body2" color="textSecondary" align="center">
								{i18n.t("qrCode.pairingCodeHint")}
							</Typography>
						</>
					) : showPhoneConfirm ? (
						<>
							<Typography color="primary" gutterBottom align="center">
								{i18n.t("qrCode.confirmOnPhoneMessage")}
							</Typography>
							<CircularProgress size={24} className={classes.buttonProgress} />
						</>
					) : showPasskeyPrompt ? (
						<>
							<Typography color="primary" gutterBottom align="center">
								{i18n.t("qrCode.passkeyExtensionMessage")}
							</Typography>
							<Typography variant="body2" color="textSecondary" className={classes.extensionHint}>
								{i18n.t("qrCode.passkeyExtensionHint")}
							</Typography>
							<Button
								variant="contained"
								color="primary"
								onClick={() => openPasskeyBridge()}
							>
								{i18n.t("qrCode.openWhatsAppWeb")}
							</Button>
						</>
					) : (
						<>
							<Typography color="primary" gutterBottom>
								{i18n.t("qrCode.message")}
							</Typography>
							{showQrCode && !isLoading ? (
								<QRCode value={qrCode} size={256} />
							) : (
								<CircularProgress size={24} className={classes.buttonProgress} />
							)}
						</>
					)}
				</Paper>
			</DialogContent>
		</Dialog>
	);
};

export default React.memo(QrcodeModal);
